import * as React from 'react'

import { Box, Icon, Tag, Text, Tooltip, useToggle } from '@factorialco/deprecated-design-system'

import type { PrivateProps as OptionProps } from 'modules/competencies/components/CompetenciesFinder/Combobox/BaseCombobox/Option'

import DropdownArrow from './DropdownArrow'
import Placeholder from './Placeholder'

type TooltipTagProps = {
  tooltip: {
    title?: string
    description: string
  }
}
const TooltipTag = ({ tooltip }: TooltipTagProps) => {
  return (
    <Box gap="s4">
      {tooltip.title && (
        <Text.Normal size="200" weight="bold">
          {tooltip.title}
        </Text.Normal>
      )}
      <Text.Normal size="200">{tooltip.description}</Text.Normal>
    </Box>
  )
}

interface Props {
  'aria-label'?: string
  placeholder: string
  options: React.ReactElement<OptionProps>[]
  value: string[]
  onChange: (value: string[]) => void
  icon?: React.ComponentProps<typeof Icon.Medium>['icon']
  disabled?: boolean
}

const OptionTag = ({
  value,
  option,
  disabled,
  onChange,
}: {
  value: string[]
  option: OptionProps
  disabled: boolean
  onChange: (value: string[]) => void
}) => {
  const [open, toggle] = useToggle(false)
  const isDisabled = disabled || option.isDisabled
  const handleClose = isDisabled
    ? undefined
    : (event: React.SyntheticEvent) => {
        const updatedValues = value.filter((v) => v !== option.value)
        onChange(updatedValues)
        event.stopPropagation()
      }

  if (option?.tooltip) {
    return (
      <Box role="listitem" aria-label={option.label}>
        <Tooltip.Dialog
          attachment="top center"
          targetAttachment="top center"
          offset="-16px 0"
          open={!!open}
          toggle={toggle}
          label={<TooltipTag tooltip={option?.tooltip} />}
          size="big"
          paddingX="s4"
          paddingY="s8"
        >
          <Tag
            inline
            rightItem="close"
            type={isDisabled ? 'grey' : 'viridian'}
            onClose={handleClose}
            icon={option.icon}
            leftItem={!!option.icon ? 'icon' : undefined}
          >
            {option.label}
          </Tag>
        </Tooltip.Dialog>
      </Box>
    )
  }

  return (
    <Box role="listitem" aria-label={option.label}>
      <Tag
        inline
        rightItem="close"
        type={isDisabled ? 'grey' : 'viridian'}
        onClose={handleClose}
        icon={option.icon}
        leftItem={!!option.icon ? 'icon' : undefined}
      >
        {option.label}
      </Tag>
    </Box>
  )
}

function TagsInput({
  'aria-label': ariaLabel,
  placeholder,
  options,
  value,
  onChange,
  icon,
  disabled = false,
}: Props) {
  const tags = options
    .filter((option) => value.includes(option.props.value))
    .map((option) => (
      <OptionTag
        key={String(option.props.value)}
        value={value}
        option={option.props}
        disabled={disabled}
        onChange={onChange}
      />
    ))

  return (
    <Box flexDirection="row" minHeight="s40" padding="s4" cursor="pointer">
      <Box
        width="full"
        flexDirection="row"
        alignItems="center"
        gap="s4"
        padding="s4"
        flexGrow
        flexWrap="wrap"
        overflowX="hidden"
        role="list"
        aria-label={ariaLabel}
      >
        {tags.length ? tags : <Placeholder icon={icon} placeholder={placeholder} value={value} />}
      </Box>
      <DropdownArrow />
    </Box>
  )
}

export default TagsInput
